<template>
  <div>
    <!-- Discounts Modal -->
    <div v-if="show" class="modal-overlay" @click="closeModal">
      <div class="modal-content discounts-modal" @click.stop>
        <div class="modal-header">
          <h3>Discounts - {{ planName }}</h3>
          <button class="modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="discounts-header">
            <button class="btn btn-primary" @click="openAddDiscountForm">
              Add Discount
            </button>
          </div>
          
          <div v-if="loading" class="loading">
            Loading discounts...
          </div>
          <div v-else-if="error" class="error-message">
            {{ error }}
          </div>
          <div v-else-if="discounts && discounts.length === 0" class="no-discounts">
            No discounts found.
          </div>
          <div v-else-if="discounts && discounts.length" class="discounts-list">
            <div class="discounts-table-header">
              <div class="discount-col">Active</div>
              <div class="discount-col">Type</div>
              <div class="discount-col">Value</div>
              <div class="discount-col">Valid From</div>
              <div class="discount-col">Valid To</div>
              <div class="discount-col">Cycle Count</div>
              <div class="discount-col actions-col">Actions</div>
            </div>
            <template v-for="discount in discounts" :key="discount.id">
              <div class="discount-row-wrapper">
                <div v-if="editingDiscountId === discount.id && saveError" class="discount-error-row">
                  <div class="error-message-inline">{{ saveError }}</div>
                </div>
                <div class="discount-row" :class="{ 'editing': editingDiscountId === discount.id }">
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id" :class="['status-badge', discount.isActive ? 'active' : 'inactive']">
                    {{ discount.isActive ? 'Active' : 'Inactive' }}
                  </span>
                  <label v-else class="checkbox-label-inline">
                    <input
                      type="checkbox"
                      v-model="editingFormData.isActive"
                      class="form-checkbox"
                    />
                    <span>Active</span>
                  </label>
                </div>
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id">{{ discount.discountType }}</span>
                  <select v-else v-model="editingFormData.discountType" class="form-select-inline">
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id">{{ discount.discountValue }}</span>
                  <input
                    v-else
                    type="number"
                    v-model.number="editingFormData.discountValue"
                    min="0"
                    step="0.001"
                    class="form-input-inline"
                  />
                </div>
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id">{{ formatDateSimple(discount.validFrom) }}</span>
                  <input
                    v-else
                    type="date"
                    v-model="editingFormData.validFrom"
                    class="form-input-inline"
                  />
                </div>
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id">{{ formatDateSimple(discount.validTo) }}</span>
                  <input
                    v-else
                    type="date"
                    v-model="editingFormData.validTo"
                    class="form-input-inline"
                  />
                </div>
                <div class="discount-col">
                  <span v-if="editingDiscountId !== discount.id">{{ discount.cycleCount }}</span>
                  <input
                    v-else
                    type="number"
                    v-model.number="editingFormData.cycleCount"
                    min="1"
                    class="form-input-inline"
                  />
                </div>
                <div class="discount-col actions-col">
                  <template v-if="editingDiscountId !== discount.id">
                    <button class="btn btn-small" @click="startEditDiscount(discount)" title="Edit">Edit</button>
                    <button class="btn btn-small btn-danger" @click="confirmDeleteDiscount(discount)" title="Delete">Delete</button>
                  </template>
                  <template v-else>
                    <button class="btn btn-small btn-primary" @click="saveDiscount(discount)" title="Save" :disabled="saving">Save</button>
                    <button class="btn btn-small btn-secondary" @click="cancelEdit" title="Cancel" :disabled="saving">Cancel</button>
                  </template>
                </div>
              </div>
            </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Discount Modal -->
    <div v-if="showDiscountForm" class="modal-overlay" @click="closeDiscountForm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingDiscount ? 'Edit Discount' : 'Add Discount' }}</h3>
          <button class="modal-close" @click="closeDiscountForm">&times;</button>
        </div>
        <form @submit.prevent="handleSaveDiscount" class="create-form">
          <div class="form-group">
            <label for="discountIsActive" class="checkbox-label">
              <input
                type="checkbox"
                id="discountIsActive"
                v-model="formData.isActive"
                class="form-checkbox"
              />
              <span>Active</span>
            </label>
          </div>
          <div class="form-group">
            <label for="discountType">Discount Type:</label>
            <select
              id="discountType"
              v-model="formData.discountType"
              required
              class="form-select"
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed</option>
            </select>
          </div>
          <div class="form-group">
            <label for="discountValue">Discount Value:</label>
            <input
              type="number"
              id="discountValue"
              v-model.number="formData.discountValue"
              required
              min="0"
              step="0.001"
              placeholder="Enter discount value"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="validFrom">Valid From:</label>
            <input
              type="date"
              id="validFrom"
              v-model="formData.validFrom"
              required
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="validTo">Valid To:</label>
            <input
              type="date"
              id="validTo"
              v-model="formData.validTo"
              required
              class="form-input"
            />
          </div>
          <div v-if="saveError" class="error-message">
            {{ saveError }}
          </div>
          <div class="form-group">
            <label for="cycleCount">Cycle Count:</label>
            <input
              type="number"
              id="cycleCount"
              v-model.number="formData.cycleCount"
              required
              min="1"
              placeholder="Enter cycle count"
              class="form-input"
            />
          </div>
          <div class="button-group">
            <button type="button" @click="closeDiscountForm" class="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" :disabled="saving" class="btn btn-primary">
              {{ saving ? 'Saving...' : (editingDiscount ? 'Update' : 'Create') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Discount Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Delete Discount</h3>
          <button class="modal-close" @click="closeDeleteModal">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete this discount?</p>
          <p class="warning-text">This action cannot be undone.</p>
          <div v-if="deleteError" class="error-message">
            {{ deleteError }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="closeDeleteModal" class="btn btn-secondary" :disabled="deleting">
            Cancel
          </button>
          <button type="button" @click="handleDeleteDiscount" :disabled="deleting" class="btn btn-danger">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'

export default {
  name: 'SubscriptionDiscounts',
  props: {
    planId: {
      type: String,
      required: true
    },
    planName: {
      type: String,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter()
    const discounts = ref([])
    const loading = ref(false)
    const error = ref('')
    const showDiscountForm = ref(false)
    const editingDiscount = ref(null)
    const editingDiscountId = ref(null)
    const saving = ref(false)
    const saveError = ref('')
    const formData = ref({
      isActive: true,
      discountType: 'percentage',
      discountValue: 0,
      validFrom: '',
      validTo: '',
      cycleCount: 1
    })
    const editingFormData = ref({
      isActive: true,
      discountType: 'percentage',
      discountValue: 0,
      validFrom: '',
      validTo: '',
      cycleCount: 1
    })
    const showDeleteModal = ref(false)
    const discountToDelete = ref(null)
    const deleting = ref(false)
    const deleteError = ref('')

    const formatDateSimple = (dateString) => {
      // Return date in YYYY-MM-DD format
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const fetchDiscounts = async () => {
      if (!props.planId) return

      try {
        loading.value = true
        error.value = ''
        const data = await api(`/admin/subscription-plans/${props.planId}/discounts`, {}, router)
        if (Array.isArray(data)) {
          discounts.value = data
        } else {
          discounts.value = []
        }
      } catch (err) {
        console.error('Failed to fetch discounts:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const openAddDiscountForm = () => {
      editingDiscount.value = null
      editingDiscountId.value = null
      formData.value = {
        isActive: true,
        discountType: 'percentage',
        discountValue: 0,
        validFrom: '',
        validTo: '',
        cycleCount: 1
      }
      saveError.value = ''
      showDiscountForm.value = true
    }

    const startEditDiscount = (discount) => {
      editingDiscountId.value = discount.id
      editingDiscount.value = discount
      editingFormData.value = {
        isActive: discount.isActive,
        discountType: discount.discountType,
        discountValue: discount.discountValue,
        validFrom: formatDateSimple(discount.validFrom),
        validTo: formatDateSimple(discount.validTo),
        cycleCount: discount.cycleCount
      }
      saveError.value = ''
    }

    const cancelEdit = () => {
      editingDiscountId.value = null
      editingDiscount.value = null
      saveError.value = ''
    }

    const saveDiscount = async (discount) => {
      if (!props.planId) return

      saving.value = true
      saveError.value = ''

      try {
        const payload = {
          isActive: editingFormData.value.isActive,
          discountType: editingFormData.value.discountType,
          discountValue: editingFormData.value.discountValue,
          validFrom: editingFormData.value.validFrom,
          validTo: editingFormData.value.validTo,
          cycleCount: editingFormData.value.cycleCount
        }

        await api(`/admin/subscription-plans/${props.planId}/discounts/${discount.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        }, router)

        // Refresh discounts list
        await fetchDiscounts()
        cancelEdit()
      } catch (err) {
        console.error('Failed to save discount:', err)
        saveError.value = err.code || 'Something went wrong please contact support'
      } finally {
        saving.value = false
      }
    }

    const editDiscount = (discount) => {
      editingDiscount.value = discount
      formData.value = {
        isActive: discount.isActive,
        discountType: discount.discountType,
        discountValue: discount.discountValue,
        validFrom: formatDateSimple(discount.validFrom),
        validTo: formatDateSimple(discount.validTo),
        cycleCount: discount.cycleCount
      }
      saveError.value = ''
      showDiscountForm.value = true
    }

    const closeDiscountForm = () => {
      showDiscountForm.value = false
      editingDiscount.value = null
      saveError.value = ''
    }

    const handleSaveDiscount = async () => {
      if (!props.planId) return

      saving.value = true
      saveError.value = ''

      try {
        const payload = {
          isActive: formData.value.isActive,
          discountType: formData.value.discountType,
          discountValue: formData.value.discountValue,
          validFrom: formData.value.validFrom,
          validTo: formData.value.validTo,
          cycleCount: formData.value.cycleCount
        }

        if (editingDiscount.value) {
          // Update existing discount
          await api(`/admin/subscription-plans/${props.planId}/discounts/${editingDiscount.value.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
          }, router)
        } else {
          // Create new discount
          await api(`/admin/subscription-plans/${props.planId}/discounts`, {
            method: 'POST',
            body: JSON.stringify(payload)
          }, router)
        }

        // Close form and refresh discounts list
        closeDiscountForm()
        await fetchDiscounts()
      } catch (err) {
        console.error('Failed to save discount:', err)
        saveError.value = err.code || 'Something went wrong please contact support'
      } finally {
        saving.value = false
      }
    }

    const confirmDeleteDiscount = (discount) => {
      discountToDelete.value = discount
      showDeleteModal.value = true
      deleteError.value = ''
    }

    const closeDeleteModal = () => {
      showDeleteModal.value = false
      discountToDelete.value = null
      deleteError.value = ''
    }

    const handleDeleteDiscount = async () => {
      if (!discountToDelete.value || !props.planId) return

      deleting.value = true
      deleteError.value = ''

      try {
        await api(`/admin/subscription-plans/${props.planId}/discounts/${discountToDelete.value.id}`, {
          method: 'DELETE'
        }, router)

        // Close modal and refresh discounts list
        closeDeleteModal()
        await fetchDiscounts()
      } catch (err) {
        console.error('Failed to delete discount:', err)
        deleteError.value = err.code || 'Something went wrong please contact support'
      } finally {
        deleting.value = false
      }
    }

    const closeModal = () => {
      discounts.value = []
      error.value = ''
      emit('close')
    }

    // Watch for prop changes
    watch(() => props.show, (newVal) => {
      if (newVal && props.planId) {
        fetchDiscounts()
      }
    })

    watch(() => props.planId, (newVal) => {
      if (props.show && newVal) {
        fetchDiscounts()
      }
    })

    return {
      discounts,
      loading,
      error,
      showDiscountForm,
      editingDiscount,
      editingDiscountId,
      editingFormData,
      saving,
      saveError,
      formData,
      showDeleteModal,
      discountToDelete,
      deleting,
      deleteError,
      formatDateSimple,
      fetchDiscounts,
      openAddDiscountForm,
      editDiscount,
      closeDiscountForm,
      handleSaveDiscount,
      confirmDeleteDiscount,
      closeDeleteModal,
      handleDeleteDiscount,
      closeModal,
      startEditDiscount,
      cancelEdit,
      saveDiscount
    }
  }
}
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.discounts-modal {
  max-width: 900px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5em;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: var(--bg-tertiary);
}

.modal-body {
  padding: 20px;
}

.loading {
  text-align: center;
  padding: 20px;
  color: var(--text-primary);
}

.error-message {
  color: var(--error-color);
  margin-bottom: 20px;
  padding: 12px;
  background-color: var(--error-bg);
  border-radius: 4px;
  border: 1px solid var(--error-color);
}

.discounts-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.discounts-list {
  margin-top: 20px;
}

.discounts-table-header {
  display: grid;
  grid-template-columns: 80px 100px 100px 120px 120px 100px 120px;
  gap: 10px;
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  font-weight: bold;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.discount-row {
  display: grid;
  grid-template-columns: 80px 100px 100px 120px 120px 100px 120px;
  gap: 10px;
  padding: 12px;
  align-items: center;
  font-size: 14px;
}

.discount-row.editing {
  background-color: rgba(59, 130, 246, 0.1);
}

.discount-col {
  display: flex;
  align-items: center;
}

.actions-col {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
}

.status-badge.active {
  background-color: var(--success-bg);
  color: var(--success-color);
}

.status-badge.inactive {
  background-color: var(--bg-tertiary);
  color: var(--text-muted);
}

.no-discounts {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-muted);
  font-style: italic;
}

/* Form Styles */
.create-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-input,
.form-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.button-group {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background-color: var(--accent-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--accent-hover);
}

.btn-secondary {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover {
  background-color: var(--border-color);
}

.btn-danger {
  background-color: var(--error-color);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc3545;
  opacity: 0.9;
}

.btn-small {
  padding: 4px 12px;
  font-size: 12px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.modal-body p {
  margin: 0 0 12px 0;
  color: var(--text-primary);
}

.modal-body p:last-child {
  margin-bottom: 0;
}

.warning-text {
  color: var(--error-color);
  font-size: 0.9em;
}

/* Inline editing styles */
.form-input-inline,
.form-select-inline {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  box-sizing: border-box;
  font-family: inherit;
}

.form-input-inline:focus,
.form-select-inline:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.checkbox-label-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-label-inline .form-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.discount-row-wrapper {
  border-bottom: 1px solid var(--border-color);
}

.discount-row-wrapper:last-child {
  border-bottom: none;
}

.discount-error-row {
  padding: 8px 12px;
  background-color: var(--error-bg);
  border: 1px solid var(--error-color);
  border-radius: 4px;
  margin: 8px 12px;
}

.error-message-inline {
  color: var(--error-color);
  font-size: 14px;
}

@media (max-width: 768px) {
  .discounts-table-header,
  .discount-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .discount-col {
    padding: 4px 0;
  }

  .discount-col::before {
    content: attr(data-label);
    font-weight: bold;
    margin-right: 8px;
    color: var(--text-muted);
  }
}
</style>

